import React, { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

// TV static scan line animation
const scanLine = keyframes`
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(100vh);
  }
`;

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  pointer-events: none;
  overflow: hidden;
  mix-blend-mode: ${props => props.blendMode || 'overlay'};
`;

// Create dynamic flicker animation
const createFlickerAnimation = (speed) => keyframes`
  0% {
    opacity: 0.9;
    filter: brightness(1);
  }
  5% {
    opacity: 0.6;
    filter: brightness(0.8);
  }
  8% {
    opacity: 0.9;
    filter: brightness(1.2);
  }
  10% {
    opacity: 0.8;
    filter: brightness(0.9);
  }
  18% {
    opacity: 0.9;
    filter: brightness(1);
    transform: translateX(0);
  }
  20% {
    opacity: 1;
    filter: brightness(1.1);
    transform: translateX(2px);
  }
  21% {
    transform: translateX(-1px);
  }
  22% {
    transform: translateX(0);
  }
  50% {
    opacity: 0.85;
    filter: brightness(0.95);
  }
  60% {
    opacity: 0.95;
    filter: brightness(1);
  }
  68% {
    transform: translateY(0);
  }
  70% {
    opacity: 0.9;
    filter: brightness(0.97);
    transform: translateY(3px);
  }
  71% {
    transform: translateY(0);
  }
  80% {
    opacity: 1;
    filter: brightness(1.05);
  }
  95% {
    opacity: 0.95;
    filter: brightness(1);
  }
  100% {
    opacity: 0.9;
    filter: brightness(1);
  }
`;

const StaticCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: ${props => props.opacity};
  animation: ${props => createFlickerAnimation(props.flickerSpeed)} ${props => props.flickerSpeed}s infinite;
`;

const ScanLines = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: repeating-linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0),
    rgba(0, 0, 0, 0) ${props => props.lineHeight - 1}px,
    rgba(0, 0, 0, ${props => props.lineOpacity}) ${props => props.lineHeight - 1}px,
    rgba(0, 0, 0, ${props => props.lineOpacity}) ${props => props.lineHeight}px
  );
  opacity: ${props => props.opacity};
  display: ${props => props.showScanLines ? 'block' : 'none'};
`;

const ScanLine = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: ${props => props.height}px;
  background-color: rgba(255, 255, 255, ${props => props.opacity});
  animation: ${scanLine} ${props => props.animationSpeed}s linear infinite;
  box-shadow: 0 0 15px rgba(255, 255, 255, 0.5);
  display: ${props => props.showMovingScanLine ? 'block' : 'none'};
`;

const RetroTVFuzz = ({
  // Static noise settings
  staticOpacity = 0.05,
  flickerSpeed = 3,
  
  // Scan line settings
  showScanLines = true,
  scanLineOpacity = 0.25,
  scanLineHeight = 2,
  scanLineIntensity = 0.05,
  
  // Moving scan line settings
  showMovingScanLine = true,
  movingScanLineOpacity = 0.1,
  movingScanLineHeight = 5,
  movingScanLineSpeed = 8,
  
  // Overall settings
  blendMode = 'overlay',
  brightSpotFrequency = 0.05, // 0.0 to 1.0
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;

    // Function to render static noise
    function renderNoise() {
      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Draw static noise
      const imageData = ctx.createImageData(width, height);
      const data = imageData.data;

      // Generate TV static with some color artifacts
      for (let i = 0; i < data.length; i += 4) {
        // Base grayscale value
        const gray = Math.floor(Math.random() * 255);
        
        // Add occasional color artifacts (RGB noise)
        const colorArtifact = Math.random() > 0.992;
        
        if (colorArtifact) {
          // Create colored artifact (red, green, or blue tint)
          data[i] = Math.random() > 0.5 ? 255 : gray; // R
          data[i + 1] = Math.random() > 0.5 ? 255 : gray; // G
          data[i + 2] = Math.random() > 0.5 ? 255 : gray; // B
        } else {
          // Normal grayscale static
          data[i] = gray; // R
          data[i + 1] = gray; // G
          data[i + 2] = gray; // B
        }
        
        // Alpha channel - occasional bright spots and variable transparency
        const isBrightSpot = Math.random() > (1 - brightSpotFrequency);
        data[i + 3] = isBrightSpot ? 255 : 30 + Math.floor(Math.random() * 30);
      }

      ctx.putImageData(imageData, 0, 0);
      requestAnimationFrame(renderNoise);
    }

    // Resize canvas to match window
    function resizeCanvas() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }

    // Initial setup
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
    renderNoise();

    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [brightSpotFrequency]);

  return (
    <Container blendMode={blendMode}>
      <StaticCanvas 
        ref={canvasRef} 
        opacity={staticOpacity} 
        flickerSpeed={flickerSpeed} 
      />
      <ScanLines 
        showScanLines={showScanLines}
        opacity={scanLineOpacity} 
        lineHeight={scanLineHeight} 
        lineOpacity={scanLineIntensity} 
      />
      <ScanLine 
        showMovingScanLine={showMovingScanLine}
        height={movingScanLineHeight} 
        opacity={movingScanLineOpacity} 
        animationSpeed={movingScanLineSpeed} 
      />
    </Container>
  );
};

export default RetroTVFuzz;